import { useEffect, useState } from 'react'
import { AuthProvider, useAuth } from './auth'
import { AppProvider, useAppStore } from './store'
import type { View } from './types'
import { Icon } from './components/Icons'
import { Onboarding } from './components/Onboarding'
import { CollectionView } from './views/CollectionView'
import { CoveView } from './views/CoveView'
import { FocusView } from './views/FocusView'
import { ProgressView } from './views/ProgressView'
import { SettingsView } from './views/SettingsView'
import { VisualLabPage } from './visual-lab/VisualLabPage'

const navigation: { id: Exclude<View, 'settings'>; label: string; icon: 'cove' | 'focus' | 'collection' | 'progress' }[] = [
  { id: 'cove', label: 'Cove', icon: 'cove' },
  { id: 'focus', label: 'Focus', icon: 'focus' },
  { id: 'collection', label: 'Collection', icon: 'collection' },
  { id: 'progress', label: 'Progress', icon: 'progress' },
]

type DevView = View | 'visual-lab'

function AppShell() {
  const { state, hydrated, activeSession } = useAppStore()
  const { logout } = useAuth()
  const [view, setView] = useState<DevView>('cove')
  const [showVisualLab, setShowVisualLab] = useState(false)

  useEffect(() => {
    if (activeSession) setView('focus')
  }, [activeSession?.id])

  useEffect(() => {
    document.documentElement.classList.toggle('high-contrast', state.settings.highContrast)
    document.documentElement.classList.toggle('reduced-motion', state.settings.reducedMotion)
  }, [state.settings.highContrast, state.settings.reducedMotion])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.shiftKey && e.key === 'V') {
        e.preventDefault()
        setShowVisualLab((prev) => !prev)
        if (!showVisualLab) setView('visual-lab')
      }
      if (e.key === 'Escape' && showVisualLab) {
        setShowVisualLab(false)
        setView('cove')
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [showVisualLab])

  if (!hydrated) return <div className="app-loading"><div className="loading-fish">◁</div><span>Opening your cove…</span></div>
  if (!state.onboardingComplete) return <Onboarding onReady={() => setView('focus')} />

  if (showVisualLab || view === 'visual-lab') {
    return <VisualLabPage />
  }

  return (
    <div className={`app-shell ${activeSession ? 'session-active' : ''}`}>
      {!activeSession && (
        <aside className="side-navigation">
          <button className="logo-button" onClick={() => setView('cove')} aria-label="Open ThriveCove home"><span>TC</span><strong>ThriveCove</strong></button>
          <nav aria-label="Primary navigation">
            {navigation.map((entry) => <button key={entry.id} className={view === entry.id ? 'active' : ''} onClick={() => setView(entry.id)}><Icon name={entry.icon} /><span>{entry.label}</span></button>)}
          </nav>
          <button className={`settings-nav ${view === 'settings' ? 'active' : ''}`} onClick={() => setView('settings')}><Icon name="settings" /><span>Settings</span></button>
          <button className="logout-nav" onClick={() => void logout()}><span>Sign out</span></button>
        </aside>
      )}

      <div className="app-content">
        {view === 'cove' && <CoveView navigate={setView} />}
        {view === 'focus' && <FocusView />}
        {view === 'collection' && <CollectionView />}
        {view === 'progress' && <ProgressView />}
        {view === 'settings' && <SettingsView />}
      </div>

      {!activeSession && (
        <nav className="bottom-navigation" aria-label="Primary navigation">
          {navigation.map((entry) => <button key={entry.id} className={view === entry.id ? 'active' : ''} onClick={() => setView(entry.id)}><Icon name={entry.icon} /><span>{entry.label}</span></button>)}
          <button className={view === 'settings' ? 'active' : ''} onClick={() => setView('settings')}><Icon name="settings" /><span>Settings</span></button>
        </nav>
      )}
    </div>
  )
}

export default function App() {
  const showDevVisualLab =
    import.meta.env.DEV &&
    new URLSearchParams(window.location.search).get('visualLab') === '1'

  if (showDevVisualLab) {
    return <VisualLabPage />
  }

  return (
    <AuthProvider>
      <AppProvider>
        <AppShell />
      </AppProvider>
    </AuthProvider>
  )
}
